import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { isSameDay } from "date-fns";

const DashboardSummary = () => {
    const [users, setUsers] = useState([]);
    const [providers, setProviders] = useState([]);
    const [bookings, setBookings] = useState([]);

    const [totalRevenue, setTotalRevenue] = useState(null);
    const [todayBookings, setTodayBookings] = useState(0);

    const [completedBookings, setCompletedBookings] = useState(0);
    const [rejectedBookings, setRejectedBookings] = useState(0);
    const [showBookingBreakdown, setShowBookingBreakdown] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            // 👥 USERS
            const userSnapshot = await getDocs(collection(db, "users"));
            setUsers(userSnapshot.docs);

            // 👨‍🔧 PROVIDERS
            const providerSnapshot = await getDocs(collection(db, "ServiceProviders"));
            // One doc per person now; dedupe by user so legacy per-category
            // docs for the same person still count once.
            setProviders([
                ...new Set(
                    providerSnapshot.docs.map((d) => d.data().userId || d.id)
                ),
            ]);

            // 📅 BOOKINGS
            const bookingSnapshot = await getDocs(collection(db, "bookings"));
            const grocerySnapshot = await getDocs(collection(db, "groceryOrders"));

            // Normalize service bookings
            const serviceBookings = bookingSnapshot.docs.map(doc => ({
                ...doc.data(),
                type: "booking",
                createdAt: doc.data().dateTime,
                amount: doc.data().payableAmount || 0,
                status: doc.data().status,
            }));

            // Normalize grocery orders
            const groceryBookings = grocerySnapshot.docs.map(doc => ({
                ...doc.data(),
                type: "grocery",
                createdAt: doc.data().createdAt,
                amount: doc.data().grandTotal || 0,
                status: doc.data().status,
            }));

            const allBookings = [...serviceBookings, ...groceryBookings];
            setBookings(allBookings);

            // 💰 TOTAL REVENUE
            const total = allBookings.reduce(
                (sum, b) => sum + (b.amount || 0),
                0
            );
            setTotalRevenue(total > 0 ? total : null);

            // 📅 TODAY BOOKINGS
            const today = new Date();
            const todayCount = allBookings.filter(b => {
                if (!b.createdAt) return false;
                const date = typeof b.createdAt.toDate === "function"
                    ? b.createdAt.toDate()
                    : new Date(b.createdAt);
                return isSameDay(today, date);
            }).length;

            setTodayBookings(todayCount);

            // ✅ COMPLETED / ❌ REJECTED
            setCompletedBookings(
                allBookings.filter(b => b.status === "completed").length
            );
            setRejectedBookings(
                allBookings.filter(b => b.status === "canceled").length
            );
        };

        fetchData();
    }, []);

    return (
        <>
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 p-4">
                <SummaryCard title="Total Users" value={users.length} />
                <SummaryCard title="Total Providers" value={providers.length} />

                <SummaryCard
                    title="Total Bookings"
                    value={bookings.length}
                    clickable
                    onClick={() => setShowBookingBreakdown(true)}
                />

                <SummaryCard
                    title="Total Revenue"
                    value={totalRevenue !== null ? `₹${totalRevenue}` : "Cash on Service"}
                />

                <SummaryCard title="Today's Bookings" value={todayBookings} />
            </div>

            {/* 🔍 BOOKING BREAKDOWN MODAL */}
            {showBookingBreakdown && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-80">
                        <h2 className="text-xl font-bold mb-4 text-center">
                            Booking Breakdown
                        </h2>

                        <div className="space-y-3">
                            <Row label="Completed" value={completedBookings} color="text-green-600" />
                            <Row label="Rejected" value={rejectedBookings} color="text-red-500" />

                            <div className="flex justify-between border-t pt-2 mt-2">
                                <span>Total</span>
                                <span className="font-bold">
                                    {completedBookings + rejectedBookings}
                                </span>
                            </div>
                        </div>

                        <button
                            className="mt-6 w-full bg-gray-800 text-white py-2 rounded-lg"
                            onClick={() => setShowBookingBreakdown(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

const SummaryCard = ({ title, value, clickable = false, onClick }) => (
    <div
        onClick={clickable ? onClick : undefined}
        className={`bg-white p-4 rounded-xl shadow-md text-center ${clickable ? "cursor-pointer hover:shadow-lg transition" : ""
            }`}
    >
        <h3 className="text-gray-500 text-sm">{title}</h3>
        <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
);

const Row = ({ label, value, color }) => (
    <div className="flex justify-between">
        <span>{label}</span>
        <span className={`font-bold ${color}`}>{value}</span>
    </div>
);

export default DashboardSummary;
